import React, { useEffect, useState } from 'react'
import { setStorageToken } from '../hooks'
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router'
import * as lib from '../lib'
import * as state from '../states'
import { useAtomValue, useSetAtom, useAtom } from 'jotai'
import {
	LogoIcon,
	GenerationIcon,
	SpendIcon,
	ReportIcon,
	AccountIcon,
	LogoutIcon,
	RoleIcon,
	TrackingIcon,
	IconAward,
	IconExchangeDollar,
	IconTrendingUp,
	IconLayers,
	IconSend,
	IconShare2
} from './ui'
import { Access } from '../lib'
import {
	EOfferSpendByAccountView,
	EOfferSpendByOfferView,
	EOfferSpendByOfferScopeView,
	OFFER_SPEND_BY_ACCOUNT_VIEW_LABEL_MAP,
	OFFER_SPEND_BY_OFFER_VIEW_LABEL_MAP,
	OFFER_SPEND_BY_OFFER_SCOPE_VIEW_LABEL_MAP,
	IAccountBuyer
} from '../types'
import { MENU, MENU_CATEGORY } from '../constants'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

const BREAKPOINT_XL = 1200

type IMenuItemProps = {
	hasAccess: boolean
	url: string
	urlLink?: string
	isActiveExtraCondition?: boolean
	title: string
	showTitle?: boolean
	icon: React.ReactNode
	count?: number
	classesToAdd?: {
		link?: string
		arrowBox?: string
		liTop?: string
		ulSecond?: string
	};
	onClick?: (event: React.MouseEvent) => void
	renderSecondLevelItems?: () => React.ReactNode
}

const LinkBox = styled.div`
    position: relative;
`

const ArrowBox = styled.span`
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    color: var(--headingColor);

    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    transition: var(--transition);
    opacity: 1;

    &.arrow-third-level {
        width: 30px;
        height: 30px;
    }

    &:hover {
        color: var(--mainColor);
    }

    &[data-dark='true'] {
        color: var(--whiteColor);

        &:hover {
            color: var(--mainColor);
        }
    }
`

const ArrowIcon = styled.span`
    display: inline-block;

    content: "";
    width: 7.5px;
    height: 7.5px;
    border-style: solid;
    border-width: 0 1px 1px 0;
    border-color: inherit;
    transform: rotate(-45deg);
    transform-origin: center center;
    transition: var(--transition);

    &[data-active='true'] {
        transform: rotate(45deg)
    }
`

const MenuItem = (
	props: IMenuItemProps
) => {
	const {
		hasAccess,
		url,
		urlLink,
		isActiveExtraCondition,
		title,
		showTitle = false,
		count,
		classesToAdd,
		icon,
		onClick,
		renderSecondLevelItems
	} = props

	const { pathname } = useLocation()
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)

	const _checkIsActive = () => {
		return urlLink ?
			(isActiveExtraCondition ?? true) && pathname.includes(url) :
			(isActiveExtraCondition ?? true) && pathname === url
	}

	const isActiveInit = _checkIsActive()
	const [isActive, setIsActive] = useState<boolean>(isActiveInit)
	const [isArrowActive, setIsArrowActive] = useState<boolean>(isActiveInit)

	const renderSecondLevelCondition = !!renderSecondLevelItems

	useEffect(() => {
		const isActive = _checkIsActive()

		setIsActive(isActive)
		setIsArrowActive(isActive)
		//console.log('MenuItem: useEffect:: ', { pathname, url, urlLink, isActive, isActiveExtraCondition, hasAccess, title, count })
	}, [pathname, isActiveExtraCondition])

	return (
		hasAccess ? <>
			<li
				className={[
					isActive ? 'mm-active' : '',
					classesToAdd?.liTop ?? ''
				].join(' ')}
			>
				<LinkBox>
					<Link to={urlLink ? urlLink : url}
					      className={[
						      'menu-title',
						      classesToAdd?.link ?? ''
					      ].join(' ')}
					      {...(!!onClick && { onClick })}
					>
						<span className="icon">{icon}</span>
						<span className="title" {...(showTitle && { title })}>{title}</span>

						{renderSecondLevelCondition ? (
							<span className="count">{count}</span>
						) : null}

					</Link>

					{renderSecondLevelCondition ? (
						<ArrowBox data-dark={isThemeDark}
						          className={[
							          'menu-title-arrow',
							          classesToAdd?.arrowBox ?? ''
						          ].join(' ')}
						          onClick={() => setIsArrowActive(prevVal => !prevVal)}
						>
							<ArrowIcon data-active={isArrowActive}/>
						</ArrowBox>
					) : null}
				</LinkBox>

				{renderSecondLevelCondition ? (
					<ul
						className={[
							'sidemenu-second-level',
							classesToAdd?.ulSecond ?? '',
							'mm-collapse',
							isArrowActive ? 'mm-show' : ''
						].join(' ')}
					>
						{renderSecondLevelItems()}
					</ul>
				) : null}
			</li>
		</> : null
	)
}

export const SideMenu = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const [searchParams] = useSearchParams()

	const [isMenuOpen, setIsMenuOpen] = useAtom(state.menuIsOpenAtom)
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)
	const setJotaiTokens = useSetAtom(state.tokensAtom)
	const [profile, setProfile] = useAtom(state.profileAtom)
	const $access = new Access(profile ?? null)
	const offerGroupItems = useAtomValue(state.offerGroupsAtom)
	const accountBuyerItems = useAtomValue(state.accountBuyersAtom)
	const accountTeamItems = useAtomValue(state.accountTeamsAtom)

	const accountBuyerItemsByTeamReport = Object.groupBy(
		accountBuyerItems ?? [], (item) => {
			return item?.role?.teamReport ?? 'UNKNOWN' // The return value becomes the key (coerced to string)
		})

	const viewOfferSpendByAccountItems = [
		EOfferSpendByAccountView.Day,
		EOfferSpendByAccountView.Month
	]

	const viewOfferSpendByOfferItems = [
		EOfferSpendByOfferView.Day,
		EOfferSpendByOfferView.Month
	]

	const viewOfferSpendByOfferScopeItems = [
		EOfferSpendByOfferScopeView.Day,
		EOfferSpendByOfferScopeView.Month
	]

	const group = lib.groupSearchParams(searchParams.get('group') ?? '')
	const accountTeam = lib.groupSearchParams(searchParams.get('team') ?? '')
	const buyerId = lib.buyerIdSearchParams(searchParams.get('buyer') ?? '')
	const currentBuyer = accountBuyerItems?.find(item => item.id === buyerId) ?? null
	const currentBuyerTeamReport = currentBuyer?.role?.teamReport
	const viewByAccount = lib.offerSpendViewByAccountSearchParams(searchParams.get('by') ?? EOfferSpendByAccountView.Day)
	const viewByOffer = lib.offerSpendViewByOfferSearchParams(searchParams.get('by') ?? EOfferSpendByOfferView.Day)
	const viewByOfferScope = lib.offerSpendViewByOfferScopeSearchParams(searchParams.get('by') ?? EOfferSpendByOfferScopeView.Day)

	const getFilteredSearchParamsEntries = () => {
		// save sort and filter params now
		//TODO: check the filter-list ...
		return Array.from(searchParams.entries()).filter(
			([key, value]) => ['s', 'f'].includes(key)
		)
	}

	const getToggleSideMenuClass = () => {
		return window.innerWidth >= BREAKPOINT_XL ? 'toggle-sidemenu-area' : 'active-sidemenu-area'
	}
	const [toggleSideMenuClassState, setToggleSideMenuClassState] = useState<string>(getToggleSideMenuClass())

	useEffect(() => {
		const handleResize = () => {
			setToggleSideMenuClassState(getToggleSideMenuClass())
		}

		window.addEventListener('resize', handleResize)

		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [])

	const _getFirstAccountBuyerLink = (
		teamReport: string,
		items: IAccountBuyer[] | null,
		uri: string
	) => {
		const filteredSearchParamsEntries = pathname === uri ? getFilteredSearchParamsEntries() : []
		const u = new URLSearchParams(filteredSearchParamsEntries)
		u.set('buyer', encodeURIComponent(items?.[0]?.id ?? 0))
		const to = `${uri}?${u}`
		//console.log('_getFirstAccountBuyerLink:: to = ', to)
		return to
	}

	const renderAccountBuyersList = (uri: string) => {
		//console.log('renderAccountBuyersList: ', { pathname, uri, buyerId, accountBuyerItems, accountBuyerItemsByTeamReport, currentBuyerTeamReport })
		const _renderAccountBuyersList = (
			teamReport: string,
			items: IAccountBuyer[] | null
		) => {
			return items?.map((item, index) => {
				const name = item.auth.login ?? item.email ?? '?'
				const key = `buyer-${name}-${index}`
				const filteredSearchParamsEntries = pathname === uri ? getFilteredSearchParamsEntries() : []
				const u = new URLSearchParams(filteredSearchParamsEntries)
				u.set('buyer', encodeURIComponent(item.id))
				const to = `${uri}?${u}`
				const isActive = pathname === uri && buyerId === item.id
				//console.log('renderAccountBuyersList: ', { pathname, uri, buyerId, itemId: item.id, isActive })
				return <li key={key}>
					<Link to={to} className={[
						isActive ? 'active' : '',
						!!item.isFired ? 'fired' : ''
					].join(' ')}
					      title={`[id: ${item.id}] ${name}`}
					>
						[<strong>id: {item.id}</strong>] {name}
					</Link>
				</li>
			})
		}

		return Object.entries(accountBuyerItemsByTeamReport)?.map(([teamReportName, teamReportItems], i) => {
			//extra condition for similar teamReportName in second-level items
			//for example: 'Adcore' in (/revenue/spend/byAccount && /revenue/spend) url-s
			const isActiveExtraCondition = pathname === uri && teamReportName === currentBuyerTeamReport
			//console.log('--- renderAccountBuyersList: ', { pathname, uri, buyerId, accountBuyerItems, accountBuyerItemsByTeamReport, teamReportItems, currentBuyerTeamReport, teamReportName, isActiveExtraCondition })
			return <div key={teamReportName} className={'team-report'}>
				<MenuItem hasAccess={true}
				          url={uri}
				          urlLink={_getFirstAccountBuyerLink(teamReportName, teamReportItems ?? [], uri)}
				          title={teamReportName}
				          isActiveExtraCondition={isActiveExtraCondition}
				          showTitle={true}
				          count={teamReportItems?.length ?? 0}
				          classesToAdd={{
					          link: 'link-third-level',
					          arrowBox: 'arrow-third-level',
					          liTop: 'li-third-level',
					          ulSecond: 'ul-third-level'
				          }}
				          icon={<></>}
				          renderSecondLevelItems={() => _renderAccountBuyersList(teamReportName, teamReportItems ?? [])}
				/>
			</div>
		})
	}

	const renderAccountTeamsList = (uri: string) => {
		return accountTeamItems?.map((item, index) => {
			const key = `account-team-${item}-${index}`
			const filteredSearchParamsEntries = pathname === uri ? getFilteredSearchParamsEntries() : []
			const u = new URLSearchParams(filteredSearchParamsEntries)
			//console.log('renderAccountTeamsList:: ', { pathname, u, uri, filteredSearchParamsEntries })
			u.set('team', encodeURIComponent(item))
			const to = `${uri}?${u}`
			const isActive = pathname === uri && accountTeam === item
			return (
				<li key={key}>
					<Link to={to} className={[isActive ? 'active' : ''].join(' ')}>
						{item}
					</Link>
				</li>
			)
		})
	}

	const renderMenuAffiliate = (): React.ReactNode => {
		const refOfferSpendReportPath = MENU.offerSpendReport.url
		const isPathIncludesRef = pathname.includes(refOfferSpendReportPath)

		const renderItemsTrackingCap = (uri: string) => {
			return offerGroupItems?.map((item, index) => {
				const key = `tracking-cap-${item}-${index}`
				const filteredSearchParamsEntries = pathname === uri ? getFilteredSearchParamsEntries() : []
				const u = new URLSearchParams(filteredSearchParamsEntries)
				//console.log('renderItemsTrackingCap:: ', { pathname, u, uri, filteredSearchParamsEntries })
				u.set('group', encodeURIComponent(item))
				const to = `${uri}?${u}`
				const isActive = pathname === uri && group === item
				return (
					<li key={key}>
						<Link to={to} className={[isActive ? 'active' : ''].join(' ')}>
							{item}
						</Link>
					</li>
				)
			})
		}

		const renderOfferSpendByAccount = (uri: string) => {
			const title = t(MENU.offerSpendReportByAccount.title)
			return viewOfferSpendByAccountItems.map((item, key) => {
				const filteredSearchParamsEntries =
					isPathIncludesRef && uri.includes(refOfferSpendReportPath) ? getFilteredSearchParamsEntries() : []
				const u = new URLSearchParams(filteredSearchParamsEntries)
				u.set('by', item)
				const to = `${uri}?${u}`
				const isActive = pathname === uri && viewByAccount === item
				//console.log('renderOfferSpendByAccount: ', { refOfferSpendReportPath, uri, pathname, item, u, to, viewByAccount, isActive })
				const keyTrans = OFFER_SPEND_BY_ACCOUNT_VIEW_LABEL_MAP?.[item]
				const labelTrans = t(keyTrans)

				return <li key={key}>
					<Link to={to} className={[isActive ? 'active' : ''].join(' ')}>
						{title}: {labelTrans}
					</Link>
				</li>
			})
		}

		const renderOfferSpendByOffer = (uri: string) => {
			const title = t(MENU.offerSpendReportByOffer.title)
			return viewOfferSpendByOfferItems.map((item, key) => {
				const filteredSearchParamsEntries =
					isPathIncludesRef && uri.includes(refOfferSpendReportPath) ? getFilteredSearchParamsEntries() : []
				const u = new URLSearchParams(filteredSearchParamsEntries)
				u.set('by', item)
				const to = `${uri}?${u}`
				const isActive = pathname === uri && viewByOffer === item
				//console.log('renderOfferSpendByOffer: ', {uri, pathname, item, u, to, viewByOffer, isActive})
				const keyTrans = OFFER_SPEND_BY_OFFER_VIEW_LABEL_MAP?.[item]
				const labelTrans = t(keyTrans)

				return <li key={key}>
					<Link to={to} className={[isActive ? 'active' : ''].join(' ')}>
						{title}: {labelTrans}
					</Link>
				</li>
			})
		}

		const renderOfferSpendByOfferScope = (uri: string) => {
			const title = t(MENU.offerSpendReportByOfferScope.title)
			return viewOfferSpendByOfferScopeItems.map((item, key) => {
				const filteredSearchParamsEntries =
					isPathIncludesRef && uri.includes(refOfferSpendReportPath) ? getFilteredSearchParamsEntries() : []
				const u = new URLSearchParams(filteredSearchParamsEntries)
				u.set('by', item)
				const to = `${uri}?${u}`
				const isActive = pathname === uri && viewByOfferScope === item
				//console.log('renderOfferSpendByOfferScope: ', {uri, pathname, item, u, to, viewByOfferScope, isActive})
				const keyTrans = OFFER_SPEND_BY_OFFER_SCOPE_VIEW_LABEL_MAP?.[item]
				const labelTrans = t(keyTrans)

				return <li key={key}>
					<Link to={to} className={[isActive ? 'active' : ''].join(' ')}>
						{title}: {labelTrans}
					</Link>
				</li>
			})
		}

		const _getFirstOfferSpendReportLink = () => {
			let uri: string = ''

			switch (true) {
				case MENU.offerSpendReportByAccount.access($access): {
					uri = MENU.offerSpendReportByAccount.url
					break
				}
				case MENU.offerSpendReportByOffer.access($access): {
					uri = MENU.offerSpendReportByOffer.url
					break
				}
				case MENU.offerSpendReportByOfferScope.access($access): {
					uri = MENU.offerSpendReportByOfferScope.url
					break
				}
				default: {
					break
				}
			}

			const filteredSearchParamsEntries =
				isPathIncludesRef && uri.includes(refOfferSpendReportPath) ? getFilteredSearchParamsEntries() : []
			const u = new URLSearchParams(filteredSearchParamsEntries)
			const to = `${uri}?${u}`
			//console.log('_getFirstOfferSpendReportLink:: to = ', to)
			return to
		}

		const categoryTitle = t(MENU_CATEGORY.affiliate.title)

		return MENU_CATEGORY.affiliate.access($access) ? (
			<>
				<li><span className="cat">{categoryTitle}</span></li>

				<MenuItem hasAccess={MENU.offerScope.access($access)}
				          url={MENU.offerScope.url}
				          title={t(MENU.offerScope.title)}
				          count={offerGroupItems?.length ?? 0}
				          icon={<TrackingIcon/>}
				          renderSecondLevelItems={() => renderItemsTrackingCap(MENU.offerScope.url)}
				/>

				<MenuItem hasAccess={MENU.offerSpend.access($access)}
				          url={MENU.offerSpend.url}
				          title={t(MENU.offerSpend.title)}
				          count={accountBuyerItems?.length ?? 0}
				          icon={<SpendIcon/>}
				          renderSecondLevelItems={() => renderAccountBuyersList(MENU.offerSpend.url)}
				/>

				<MenuItem hasAccess={MENU.offerSpendReport.access($access)}
				          url={MENU.offerSpendReport.url}
				          urlLink={_getFirstOfferSpendReportLink()}
				          title={t(MENU.offerSpendReport.title)}
				          showTitle={true}
				          count={
					          (MENU.offerSpendReportByAccount.access($access) ? viewOfferSpendByAccountItems.length : 0) +
					          (MENU.offerSpendReportByOffer.access($access) ? viewOfferSpendByOfferItems.length : 0) +
					          (MENU.offerSpendReportByOfferScope.access($access) ? viewOfferSpendByOfferScopeItems.length : 0)
				          }
				          icon={<ReportIcon/>}
				          renderSecondLevelItems={() => (<>
					          {MENU.offerSpendReportByAccount.access($access) ? renderOfferSpendByAccount(MENU.offerSpendReportByAccount.url) : null}

					          {MENU.offerSpendReportByOffer.access($access) ? renderOfferSpendByOffer(MENU.offerSpendReportByOffer.url) : null}

					          {MENU.offerSpendReportByOfferScope.access($access) ? renderOfferSpendByOfferScope(MENU.offerSpendReportByOfferScope.url) : null}
				          </>)}
				/>

				<MenuItem hasAccess={MENU.postbackSend.access($access)}
				          url={MENU.postbackSend.url}
				          title={t(MENU.postbackSend.title)}
				          showTitle={true}
				          icon={<IconSend/>}
				/>

				<br/>
				<br/>
			</>
		) : null
	}

	const renderMenuRevenue = (): React.ReactNode => {
		const _getFirstAccountTeamLink = (uri: string) => {
			const filteredSearchParamsEntries = pathname === uri ? getFilteredSearchParamsEntries() : []
			const u = new URLSearchParams(filteredSearchParamsEntries)
			u.set('team', encodeURIComponent(accountTeamItems?.[0] ?? 'NO_TEAM'))
			const to = `${uri}?${u}`
			//console.log('_getFirstAccountTeamLink:: to = ', to)
			return to
		}

		const categoryTitle = t(MENU_CATEGORY.revenue.title)
		const firstAccountBuyerTeamReport = Object.keys(accountBuyerItemsByTeamReport)?.[0] ?? 'UNKNOWN'
		// render category if user has access at least for 1 or more menu items
		return MENU_CATEGORY.revenue.access($access) ? (
			<>
				<li><span className="cat">{categoryTitle}</span></li>

				<MenuItem hasAccess={MENU.revenueAgency.access($access)}
				          url={MENU.revenueAgency.url}
				          title={t(MENU.revenueAgency.title)}
				          icon={<IconAward/>}
				/>

				<MenuItem hasAccess={MENU.affiliate.access($access)}
				          url={MENU.affiliate.url}
				          title={t(MENU.affiliate.title)}
				          icon={<IconShare2/>}
				/>

				<MenuItem hasAccess={MENU.revenueSpend.access($access)}
				          url={MENU.revenueSpend.url}
				          title={t(MENU.revenueSpend.title)}
				          count={accountBuyerItems?.length ?? 0}
				          icon={<IconExchangeDollar size={20}/>}
				          renderSecondLevelItems={() => renderAccountBuyersList(MENU.revenueSpend.url)}
				/>

				<MenuItem hasAccess={MENU.revenueSpendReportByAccount.access($access)}
				          url={MENU.revenueSpendReportByAccount.url}
				          urlLink={_getFirstAccountBuyerLink(firstAccountBuyerTeamReport, accountBuyerItems, MENU.revenueSpendReportByAccount.url)}
				          title={t(MENU.revenueSpendReportByAccount.title)}
				          showTitle={true}
				          count={accountBuyerItems?.length ?? 0}
				          icon={<IconTrendingUp/>}
				          renderSecondLevelItems={() => renderAccountBuyersList(MENU.revenueSpendReportByAccount.url)}
				/>

				<MenuItem hasAccess={MENU.revenueSpendReportByTeam.access($access)}
				          url={MENU.revenueSpendReportByTeam.url}
				          urlLink={_getFirstAccountTeamLink(MENU.revenueSpendReportByTeam.url)}
				          title={t(MENU.revenueSpendReportByTeam.title)}
				          showTitle={true}
				          count={accountTeamItems?.length ?? 0}
				          icon={<IconLayers/>}
				          renderSecondLevelItems={() => renderAccountTeamsList(MENU.revenueSpendReportByTeam.url)}
				/>

				<br/>
				<br/>
			</>
		) : null
	}

	const renderMenuGen = (): React.ReactNode => {
		return <>
			<li><span className="cat">?</span></li>
			<li>
				<Link to={`/offer/name/generation`} className="menu-title">
					<span className="icon">
						<GenerationIcon/>
					</span>
					<span className="title">Generation offer name</span>
				</Link>
			</li>
			<li>
				<Link to={`/postback/generation`} className="menu-title">
					<span className="icon">
						<GenerationIcon/>
					</span>
					<span className="title">Postback generation</span>
				</Link>
			</li>
		</>
	}

	const renderMenuProfile = (): React.ReactNode => {
		const onLogout = (event: React.MouseEvent): void => {
			//TODO: check the logic --- to reset ALL data from Jotai state too (commonOptions...)???
			event.preventDefault()
			setProfile(null)
			setJotaiTokens({})
			setStorageToken(null)
			navigate(MENU.logout.url)
		}

		const categoryTitle = t(MENU_CATEGORY.profile.title)

		return MENU_CATEGORY.profile.access($access) ? (
			<>
				<li><span className="cat">{categoryTitle}</span></li>
				<MenuItem hasAccess={MENU.account.access($access)}
				          url={MENU.account.url}
				          title={t(MENU.account.title)}
				          icon={<AccountIcon/>}
				/>

				<MenuItem hasAccess={MENU.role.access($access)}
				          url={MENU.role.url}
				          title={t(MENU.role.title)}
				          icon={<RoleIcon/>}
				/>

				<MenuItem hasAccess={MENU.logout.access($access)}
				          url={MENU.logout.url}
				          title={t(MENU.logout.title)}
				          icon={<LogoutIcon/>}
				          onClick={onLogout}
				/>
			</>
		) : null
	}

	const renderResponsiveBurgerMenuBtn = () => {
		const onResponsiveBurgerMenuBtnClick = (event: React.MouseEvent) => {
			setIsMenuOpen(state.toggleMenu)
		}

		return (
			<div className={`responsive-burger-menu d-block d-xl-none ${isMenuOpen ? 'active' : ''}`}
			     onClick={onResponsiveBurgerMenuBtnClick}
			>
				<span className="top-bar"></span>
				<span className="middle-bar"></span>
				<span className="bottom-bar"></span>
			</div>
		)
	}

	const logoIcon = <LogoIcon size={36} fill={isThemeDark ? 'white' : 'black'}/>
	const logoTitle = 'INV CRM'
	const Logo = <>
		{logoIcon}
		<span className="ms-3 logo-text">{logoTitle}</span>
	</>

	return <>
		<div className={`sidebar-menu-area metismenu ${isMenuOpen ? toggleSideMenuClassState : ''}`}>
			<div
				className="d-flex justify-content-between align-items-center border-bottom border-color bg-white position-sticky top-0 z-1 main-logo-wrap">
				{/*TODO: check the link for MAIN-LOGO click*/}
				<Link to="/" className="main-logo d-flex align-items-center text-decoration-none">
					{Logo}
				</Link>

				{renderResponsiveBurgerMenuBtn()}
			</div>

			<ul className="sidebar-menu scroll">
				{renderMenuAffiliate()}
				{renderMenuRevenue()}
				{/* { renderMenuGen() } */}
				{renderMenuProfile()}
				<br/>

			</ul>
		</div>
	</>
}